import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnInit,
    Output
} from '@angular/core';
import {
    ApiClientService,
    CachedValuesService,
    getTableSettingKey,
    SessionStorageKey,
    SessionStorageService
} from '@services/index';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, map, of } from 'rxjs';
import { SelectedFilter, EMPTY_FILTER, FilterData, FilterConfig } from './filter-model';
import { simpleClone } from '@helpers/object-helper';
import { Device, LocationModel, Tenant, User } from 'app/types';
import { MeasurementDataFilter } from 'app/enums';
import { DateTime } from 'luxon';
import { DateRange } from '@shared/components/date-range/date-range.component';
import { convertFilterToPredicates } from './filter-utils';

export enum TableType {
    Measurements = 'measurements',
    Dashboard = 'dashboard'
}

@UntilDestroy()
@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FilterComponent implements OnInit {
    @Input() public config: FilterConfig = { fields: [] };
    @Input() public tableType: TableType;
    @HostBinding('class.visible') @Input() public visible = false;
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);

    private _storedFilter: SelectedFilter = simpleClone(EMPTY_FILTER);
    public get storedFilter() {
        return this._storedFilter;
    }

    public set storedFilter(filter: SelectedFilter) {
        this._storedFilter = createValidFilter(filter);
    }
    public selectedFilter: SelectedFilter = simpleClone(EMPTY_FILTER);
    public data: FilterData = simpleClone<FilterData>(EMPTY_FILTER);
    @Output() public filterChange: EventEmitter<SelectedFilter> = new EventEmitter<SelectedFilter>();
    private isEmpty = true;
    @Output() public isFilterEmptyChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    public showAdvancedFilters = false;

    public readonly users$: Observable<MultiSelectItem[]> = this.cachedValuesService.users$.pipe(
        map((data) =>
            data.map((item: User) => {
                return {
                    id: item.id,
                    name: item.firstName + ' ' + item.lastName // + ' - ' + item.email
                } as MultiSelectItem;
            })
        )
    );

    public readonly devices$: Observable<MultiSelectItem[]> = this.cachedValuesService.devices$.pipe(
        map((data) =>
            data.map((item: Device) => {
                return {
                    id: item.id,
                    name: item.serialNumber
                } as MultiSelectItem;
            })
        )
    );

    public readonly locations$: Observable<MultiSelectItem[]> = this.cachedValuesService.locations$.pipe(
        map((data) =>
            data.map((item: LocationModel) => {
                return {
                    id: item.id,
                    name: item.name
                } as MultiSelectItem;
            })
        )
    );

    public readonly tenants$: Observable<MultiSelectItem[]> = this.cachedValuesService.tenants$.pipe(
        map((data) =>
            data.map((item: Tenant) => {
                return {
                    id: item.id,
                    name: item.name
                } as MultiSelectItem;
            })
        )
    );

    public readonly firstAmplitude$: Observable<MultiSelectItem[]> = of([
        {
            id: MeasurementDataFilter.FirstAmplitudeNotAvailable,
            name: this.translateService.instant(
                'FILTER.MEASUREMENT_DATA_FILTERS.FIRST_AMPLITUDE_NOT_AVAILABLE'
            ) as string
        },
        {
            id: MeasurementDataFilter.FirstAmplitude0To5,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_AMPLITUDE0_TO5') as string
        },
        {
            id: MeasurementDataFilter.FirstAmplitude5To8,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_AMPLITUDE5_TO8') as string
        },
        {
            id: MeasurementDataFilter.FirstAmplitude8Plus,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_AMPLITUDE8_PLUS') as string
        }
    ]);

    public readonly lastAmplitude$: Observable<MultiSelectItem[]> = of([
        {
            id: MeasurementDataFilter.LastAmplitudeNotAvailable,
            name: this.translateService.instant(
                'FILTER.MEASUREMENT_DATA_FILTERS.LAST_AMPLITUDE_NOT_AVAILABLE'
            ) as string
        },
        {
            id: MeasurementDataFilter.LastAmplitude0To5,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_AMPLITUDE0_TO5') as string
        },
        {
            id: MeasurementDataFilter.LastAmplitude5To8,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_AMPLITUDE5_TO8') as string
        },
        {
            id: MeasurementDataFilter.LastAmplitude8Plus,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_AMPLITUDE8_PLUS') as string
        }
    ]);

    public readonly firstResult$: Observable<MultiSelectItem[]> = of([
        {
            id: MeasurementDataFilter.FirstResultTofc,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_RESULT_TOFC') as string
        },
        {
            id: MeasurementDataFilter.FirstResultST,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_RESULT_ST') as string
        },
        {
            id: MeasurementDataFilter.FirstResult0To40,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_RESULT0_TO40') as string
        },
        {
            id: MeasurementDataFilter.FirstResult40To80,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_RESULT40_TO80') as string
        },
        {
            id: MeasurementDataFilter.FirstResult80To90,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_RESULT80_TO90') as string
        },
        {
            id: MeasurementDataFilter.FirstResult90Plus,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.FIRST_RESULT90_PLUS') as string
        }
    ]);

    public readonly lastResult$: Observable<MultiSelectItem[]> = of([
        {
            id: MeasurementDataFilter.LastResultTofc,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT_TOFC') as string
        },
        {
            id: MeasurementDataFilter.LastResultST,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT_ST') as string
        },
        {
            id: MeasurementDataFilter.LastResultPTC,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT_PTC') as string
        },
        {
            id: MeasurementDataFilter.LastResult0To40,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT0_TO40') as string
        },
        {
            id: MeasurementDataFilter.LastResult40To80,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT40_TO80') as string
        },
        {
            id: MeasurementDataFilter.LastResult80To90,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT80_TO90') as string
        },
        {
            id: MeasurementDataFilter.LastResult90Plus,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.LAST_RESULT90_PLUS') as string
        }
    ]);

    public readonly ptcUse$: Observable<MultiSelectItem[]> = of([
        {
            id: MeasurementDataFilter.PtcUseYes,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.PTC_USE_YES') as string
        },
        {
            id: MeasurementDataFilter.PtcUseNo,
            name: this.translateService.instant('FILTER.MEASUREMENT_DATA_FILTERS.PTC_USE_NO') as string
        }
    ]);

    public ngOnInit() {
        this.storedFilter = this.sessionStorageService.getObject<SelectedFilter>(
            getTableSettingKey(SessionStorageKey.TableFilter, this.tableType),
            deserializeDateRange
        );

        this.initializeFilters();
        this.emitIsFilterEmpty(this.storedFilter);
        this.updateStoredFilter();
    }

    public getPredicates() {
        return convertFilterToPredicates(this.selectedFilter);
    }

    private initializeFilters() {
        this.selectedFilter = this.cloneFilterPreservingDateRange(this.storedFilter);
        this.filterChange.emit(this.selectedFilter);
    }

    public setFilterValue(key: string, value: number | string) {
        let parsedValue = value;
        if (key !== 'responsible') {
            parsedValue = typeof value === 'string' ? parseInt(value, 10) : value;
        }

        if (Array.isArray(EMPTY_FILTER[key])) {
            const items: MultiSelectId[] = [];
            items.push(parsedValue);
            this.selectedFilter[key] = items;
        } else {
            this.selectedFilter[key] = parsedValue;
        }

        this.updateStoredFilter();
    }

    public onMultiSelectChange(key: string, items: MultiSelectId[]) {
        this.selectedFilter[key] = items;
        this.updateStoredFilter();
    }

    public onSelectChange(key: string, item: MultiSelectId) {
        this.selectedFilter[key] = item;
        this.updateStoredFilter();
    }

    public onDateRangeChange(key: string, dateRange: DateRange) {
        const selecteFilterDateRange = this.selectedFilter[key] as { from: DateTime | null; to: DateTime | null };
        const isDateRangeChanged =
            selecteFilterDateRange.from?.toString() != dateRange?.from?.toString() ||
            selecteFilterDateRange.to?.toString() != dateRange?.to?.toString();
        selecteFilterDateRange.from = dateRange?.from ?? null;
        selecteFilterDateRange.to = dateRange?.to ?? null;
        this.updateStoredFilter(isDateRangeChanged);
    }

    public applyFilter(filter: SelectedFilter) {
        this.selectedFilter = this.cloneFilterPreservingDateRange(filter);
        this.updateStoredFilter(true);
    }

    public clearFilter() {
        this.selectedFilter = simpleClone(EMPTY_FILTER);
        this.updateStoredFilter(true);
    }

    private updateStoredFilter(forceUpdate?: boolean) {
        if (this.isFilterChanged() || forceUpdate) {
            this.storedFilter = this.cloneFilterPreservingDateRange(this.selectedFilter);
            const filterToStore = this.selectedFilter;
            this.sessionStorageService.setObject(
                getTableSettingKey(SessionStorageKey.TableFilter, this.tableType),
                filterToStore
            );
            this.emitIsFilterEmpty(this.selectedFilter);
            this.filterChange.emit(this.selectedFilter);
        }
    }

    private isFilterChanged() {
        return Object.keys(this.selectedFilter).some((key) => {
            const filterKey = key as keyof typeof this.selectedFilter;
            return JSON.stringify(this.selectedFilter[filterKey]) !== JSON.stringify(this.storedFilter[filterKey]);
        });
    }

    private emitIsFilterEmpty(filter: SelectedFilter) {
        if (this.isEmpty !== this.isFilterEmpty(filter)) {
            this.isEmpty = !this.isEmpty;
            this.isFilterEmptyChanged.emit(this.isEmpty);
        }
    }

    private isFilterEmpty(filter: SelectedFilter) {
        if (this.config.fields.includes('captureDateRange') || this.config.fields.includes('uploadDateRange')) {
            return JSON.stringify(filter) === JSON.stringify(EMPTY_FILTER);
        }
        return (
            JSON.stringify({ ...filter, captureDateRange: null, uploadDateRange: null }) ===
            JSON.stringify({ ...EMPTY_FILTER, captureDateRange: null, uploadDateRange: null })
        );
    }

    private cloneFilterPreservingDateRange(filter: SelectedFilter): SelectedFilter {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
        const cloned = simpleClone<SelectedFilter>(filter);
        // Preserve DateTime objects for captureDateRange
        cloned.captureDateRange = {
            from: filter.captureDateRange?.from ?? null,
            to: filter.captureDateRange?.to ?? null
        };
        cloned.uploadDateRange = {
            from: filter.uploadDateRange?.from ?? null,
            to: filter.uploadDateRange?.to ?? null
        };
        return cloned;
    }

    public hasAdvancedFilters(): boolean {
        return (
            this.config.fields.includes('firstAmplitude') ||
            this.config.fields.includes('lastAmplitude') ||
            this.config.fields.includes('firstResult') ||
            this.config.fields.includes('lastResult') ||
            this.config.fields.includes('ptcUse')
        );
    }

    public toggleAdvancedFilters(): void {
        this.showAdvancedFilters = !this.showAdvancedFilters;
    }
}

export function deserializeDateRange(filter: SelectedFilter): SelectedFilter {
    const deserializeDateTime = (value: unknown): DateTime | null => {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            const dt = DateTime.fromISO(value);
            return dt.isValid ? dt : null;
        }
        return value as DateTime;
    };

    if (filter) {
        return {
            ...filter,
            captureDateRange: {
                from: deserializeDateTime(filter.captureDateRange?.from),
                to: deserializeDateTime(filter.captureDateRange?.to)
            },
            uploadDateRange: {
                from: deserializeDateTime(filter.uploadDateRange?.from),
                to: deserializeDateTime(filter.uploadDateRange?.to)
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
    return simpleClone<SelectedFilter>(EMPTY_FILTER);
}

export function createValidFilter(filter: SelectedFilter) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
    const validFilter = simpleClone<SelectedFilter>(EMPTY_FILTER);

    if (filter) {
        if (!Object.keys(validFilter).every((key) => Object.keys(filter).includes(key))) {
            console.warn(
                'Missing keys:',
                Object.keys(validFilter)
                    .filter((key) => !Object.keys(filter).includes(key))
                    .join(', ')
            );
        }
        if (!Object.keys(filter).every((key) => Object.keys(validFilter).includes(key))) {
            console.warn(
                'Redundant keys:',
                Object.keys(filter)
                    .filter((key) => !Object.keys(validFilter).includes(key))
                    .join(', ')
            );
        }
        for (const key of Object.keys(validFilter).filter((key) => Array.isArray(validFilter[key]))) {
            validFilter[key] = (filter?.[key] || []) as MultiSelectId[];
        }
        // for (const key of Object.keys(validFilter).filter(
        //     (key) => !Array.isArray(validFilter[key]) && key !== 'captureDateRange'
        // )) {
        //     validFilter[key] = (filter?.[key] ?? validFilter[key]) as MultiSelectId;
        // }
        validFilter.captureDateRange = filter?.captureDateRange || {
            from: null,
            to: null
        };
        validFilter.uploadDateRange = filter?.uploadDateRange || {
            from: null,
            to: null
        };
    }

    return validFilter;
}
